import { useCallback, useEffect, useState } from 'react';
import { api } from './api.js';
import { t } from '../i18n/index.js';

/**
 * Tiny API reachability indicator; stays useful in prod as a "server down" hint.
 *
 * It is a chip in the header, so it gets a word. It used to carry the whole sentence "Cannot
 * reach the server. Check your connection and try again." — which on a phone filled the header
 * and said the same thing as the note on the page underneath it. The page explains; this only
 * marks.
 */
export function HealthBadge() {
  const [state, setState] = useState<'loading' | 'ok' | 'down'>('loading');
  const check = useCallback(() => {
    api<{ ok: boolean }>('/health')
      .then((r) => setState(r.ok ? 'ok' : 'down'))
      .catch(() => setState('down'));
  }, []);

  useEffect(() => {
    check();
    // Stop saying "offline" the moment it is not true any more.
    const onOnline = () => check();
    const onOffline = () => setState('down');
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [check]);

  if (state === 'loading') return <span className="badge">{t('loading')}</span>;
  if (state === 'down') return <span className="badge bad">{t('health_offline')}</span>;
  return <span className="badge good">●</span>;
}
